import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import SiteFooter from "@/components/SiteFooter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, AUTH } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";

export default function SubscribeSuccess() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [claimedCategory, setClaimedCategory] = useState("merchant");

  // If no token, something's wrong
  if (!token) {
    return (
      <div>
        <Nav />
        <div className="max-w-md mx-auto px-6 py-32 text-center">
          <h1 className="text-3xl mb-4">Missing subscription token</h1>
          <p className="text-muted-foreground mb-6">
            We couldn't find your subscription. Please contact support or{" "}
            <a href="/pricing" className="text-primary font-bold">try again</a>.
          </p>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/claim-subscription", {
        subscribe_token: token,
        name,
        email,
        password,
      });
      AUTH.set(r.data.token, r.data.user);
      toast.success("Welcome! Your subscription is active.");
      setClaimedCategory(r.data.tenant.business_category || "merchant");
      setClaimed(true);
      // Redirect to dashboard after a beat
      setTimeout(() => {
        nav(`/dashboard/${r.data.tenant.slug}`);
      }, 1500);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not create account";
      if (detail.includes("already exists")) {
        toast.error("You already have an account. Sign in to link your subscription.");
        nav(`/subscribe/claim?token=${token}`);
      } else {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  if (claimed) {
    return (
      <div>
        <Nav />
        <div className="max-w-md mx-auto px-6 py-32 text-center">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-3xl mb-2">You're all set!</h1>
          <p className="text-muted-foreground mb-3">
            Redirecting you to your{" "}
            <span className="font-bold text-foreground">
              {claimedCategory === "service_provider"
                ? "Appointment Booking CRM"
                : "Order Management Dashboard"}
            </span>
            ...
          </p>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div>
      <Nav />
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="text-center mb-8">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-3xl mb-2">Payment successful!</h1>
          <p className="text-muted-foreground">
            Your subscription is active. One last step — create your account
            to manage everything.
          </p>
        </div>
        <Card className="p-8">
          <form onSubmit={submit} className="space-y-4">
            <Input
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="sub-success-name"
            />
            <Input
              placeholder="you@business.com"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="sub-success-email"
            />
            <Input
              placeholder="Password (8+ chars)"
              type="password"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="sub-success-password"
            />
            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 font-bold"
              data-testid="sub-success-submit"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>Create account &amp; go to dashboard <ArrowRight className="w-4 h-4 ml-1" /></>
              )}
            </Button>
          </form>
        </Card>
      </div>
      <SiteFooter />
    </div>
  );
}
